import { z } from "zod"
import type { LegalBlock, LegalDocument, LegalSection, PublishedLegalDocument } from "../content/content-types.ts"

const publicLegalPayloadSchema = z.strictObject({
  announcedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  bodyMarkdown: z.string().min(100).max(60_000),
  effectiveAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  slug: z.enum(["terms-point", "terms-privacy", "terms-service"]),
  title: z.string().min(1).max(200),
  version: z.string().regex(/^\d+\.\d+$/),
})

type PublicLegalPayload = z.infer<typeof publicLegalPayloadSchema>

const tableCells = (line: string): readonly string[] =>
  line
    .slice(1, -1)
    .split("|")
    .map((cell) => cell.trim())
const isDivider = (line: string): boolean => /^\|(?:\s*:?-+:?\s*\|)+$/.test(line)

const parseBlocks = (lines: readonly string[], sectionTitle: string): readonly LegalBlock[] => {
  const blocks: LegalBlock[] = []
  let index = 0
  while (index < lines.length) {
    const line = lines[index]?.trim() ?? ""
    if (line.length === 0) {
      index += 1
      continue
    }
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = []
      while (index < lines.length && /^\d+\.\s+/.test(lines[index]?.trim() ?? "")) {
        items.push((lines[index]?.trim() ?? "").replace(/^\d+\.\s+/, ""))
        index += 1
      }
      blocks.push({ items, kind: "ordered-list" })
      continue
    }
    if (/^-\s+/.test(line)) {
      const items: string[] = []
      while (index < lines.length && /^-\s+/.test(lines[index]?.trim() ?? "")) {
        items.push((lines[index]?.trim() ?? "").replace(/^-\s+/, ""))
        index += 1
      }
      blocks.push({ items, kind: "unordered-list" })
      continue
    }
    if (line.startsWith("|") && line.endsWith("|") && isDivider(lines[index + 1]?.trim() ?? "")) {
      const columns = tableCells(line)
      index += 2
      const rows: string[][] = []
      while (index < lines.length) {
        const row = lines[index]?.trim() ?? ""
        if (!row.startsWith("|") || !row.endsWith("|")) break
        const cells = [...tableCells(row)]
        if (cells.length !== columns.length) throw new TypeError("LEGAL_TABLE_COLUMN_MISMATCH")
        rows.push(cells)
        index += 1
      }
      blocks.push({ columns, kind: "table", label: sectionTitle, rows })
      continue
    }
    const paragraph: string[] = []
    while (index < lines.length) {
      const current = lines[index]?.trim() ?? ""
      if (current.length === 0 || /^\d+\.\s+/.test(current) || /^-\s+/.test(current) || (current.startsWith("|") && current.endsWith("|"))) break
      paragraph.push(current)
      index += 1
    }
    if (paragraph.length === 0) throw new TypeError("LEGAL_MARKDOWN_BLOCK_INVALID")
    blocks.push({ kind: "paragraph", text: paragraph.join(" ") })
  }
  return blocks
}

export const parsePublicLegalDocument = (input: unknown): PublishedLegalDocument => {
  const value = publicLegalPayloadSchema.parse(input)
  const source = value.bodyMarkdown
    .replaceAll("\r\n", "\n")
    .split("\n")
    .map((line) => line.trimEnd())
  const preambleLines: string[] = []
  const sections: LegalSection[] = []
  let title = ""
  let sectionTitle: string | null = null
  let sectionLines: string[] = []
  const finishSection = (): void => {
    if (sectionTitle === null) return
    const blocks = parseBlocks(sectionLines, sectionTitle)
    if (blocks.length === 0) throw new TypeError("LEGAL_SECTION_EMPTY")
    sections.push({ blocks, title: sectionTitle })
  }
  for (const sourceLine of source) {
    const line = sourceLine.trim()
    if (line.startsWith("# ")) {
      if (title.length !== 0) throw new TypeError("LEGAL_TITLE_DUPLICATED")
      title = line.slice(2).trim()
      continue
    }
    if (/^(공고일|시행일):/.test(line)) continue
    if (line.startsWith("## ")) {
      finishSection()
      sectionTitle = line.slice(3).trim()
      sectionLines = []
      continue
    }
    if (sectionTitle === null) preambleLines.push(sourceLine)
    else sectionLines.push(sourceLine)
  }
  finishSection()
  if (title !== value.title || sections.length === 0) throw new TypeError("LEGAL_MARKDOWN_STRUCTURE_INVALID")
  return {
    announcedAt: value.announcedAt as PublishedLegalDocument["announcedAt"],
    effectiveAt: value.effectiveAt as PublishedLegalDocument["effectiveAt"],
    preamble: parseBlocks(preambleLines, value.title),
    publishable: true,
    sections,
    slug: value.slug,
    title: value.title,
    version: value.version as PublishedLegalDocument["version"],
  }
}

export const blockedLegalDocument = (slug: PublicLegalPayload["slug"], title: string): LegalDocument => ({ announcedAt: null, effectiveAt: null, preamble: [], publishable: false, sections: [], slug, title, version: "0.1" })
